import { useMemo, useState } from "react";
import { type TrendLineSeries, TrendLines } from "@/components/charts/TrendLines";
import { DetailSection } from "@/components/shared/detail-section";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PersonReviewHistoryEntry } from "@/hooks/use-review-report";
import {
  buildPersonReviewTrend,
  type TrendGroup,
  type TrendSubject,
  UNGROUPED,
} from "@/lib/reviews/trend";

/**
 * A person's scores trended across cycles — DM-WF-001, EPIC-070 (WI-261).
 *
 * Opens on one chart per competency group, each expandable into the
 * competencies inside it: a person can carry a dozen-plus competencies, and a
 * dozen charts stacked on arrival is a wall rather than an answer (AC-740).
 *
 * Takes the history its sibling `PersonReviewHistory` already fetched rather
 * than fetching again — and with it the redaction that was already applied, so
 * this file makes no visibility decision of its own. For the subject's own
 * view the peer line is the anonymised group average the report hands over,
 * labelled as such so it is never read as one person's opinion.
 */

/**
 * CATEGORICAL, not semantic: these separate the rating sources, they do not
 * rank them.
 *
 * The brand chart ramp is a deliberately ANALOGOUS blue-to-violet series —
 * fine for five adjacent bars, too close for two lines crossing each other on
 * one plot, where `chart-1` against `chart-4` read as the same stroke twice.
 * Widening that ramp is a design-system change, so instead the manager line —
 * the one most often read — takes `--foreground`, which is as far from the
 * card as any colour gets in either theme, and the others take hues that are
 * plainly not it. Every line still carries its own dash pattern, so identity
 * never rests on colour alone.
 */
const SOURCE_STYLE: Record<string, { color: string; dash?: string }> = {
  manager: { color: "var(--foreground)" },
  self: { color: "var(--chart-1)", dash: "6 4" },
};
const POOLED_STYLE = { color: "var(--chart-4)", dash: "2 4" };
const OTHER_STYLE = { color: "var(--chart-3)", dash: "8 3" };

/**
 * The axis label comes from the period END, not the cycle name.
 *
 * Cycle names are author-written and routinely share a long prefix ("H1 2025
 * — Client Success Manager"), so clipping them to fit an axis produces three
 * identical ticks. The month the period closed is short, always distinct
 * between rounds, and is the thing being plotted against anyway. The full name
 * is still what the tooltip and the screen-reader table say.
 */
function monthLabel(periodEnd: string): string {
  const [year, month] = periodEnd.split("-").map(Number);
  if (!year || !month) return periodEnd;
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
}

/**
 * Axis ticks for the cycles, in order.
 *
 * Two rounds CAN close in the same month — a company-wide round and a
 * role-specific one, say — which would otherwise put two identical labels on
 * the axis. A repeat is numbered rather than left ambiguous; the tooltip and
 * the screen-reader table carry the full name either way.
 */
function ticksFor(
  cycles: { reviewId: string; cycleName: string; periodEnd: string }[],
): { key: string; label: string; title: string }[] {
  const seen = new Map<string, number>();
  return cycles.map((cycle) => {
    const base = monthLabel(cycle.periodEnd);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return {
      key: cycle.reviewId,
      label: count === 1 ? base : `${base} (${count})`,
      title: cycle.cycleName,
    };
  });
}

function toSeries(subject: TrendSubject): TrendLineSeries[] {
  return subject.series.map((series) => {
    const style = series.isPooled
      ? POOLED_STYLE
      : (SOURCE_STYLE[series.relationshipKey] ?? OTHER_STYLE);
    return {
      key: series.relationshipKey,
      label: series.relationshipName,
      color: style.color,
      dash: style.dash,
      points: series.points,
      note: series.isPooled ? "averaged" : undefined,
    };
  });
}

export function PersonReviewTrend({ entries }: { entries: PersonReviewHistoryEntry[] }) {
  const trend = useMemo(() => buildPersonReviewTrend(entries), [entries]);

  // Nothing to trend: one cycle, or no cycle that carried a rating. Saying so
  // would be noise on a tab that already shows the reviews themselves.
  if (trend.groups.length === 0) return null;

  const ticks = ticksFor(trend.cycles);

  return (
    <DetailSection title="Trend">
      <p className="mb-4 text-muted-foreground text-sm">
        Scores across {trend.cycles.length} cycles, on the 1–5 capability scale. A line stops where
        there is nothing comparable to join it to.
      </p>
      <div className="space-y-4">
        {trend.groups.map((group) => (
          <TrendGroupCard
            key={group.key}
            group={group}
            ticks={ticks}
            breaks={trend.breaks}
            // With a single group there is no grouping to navigate — hiding the
            // competencies behind a click would cost a step and reveal nothing.
            soleGroup={trend.groups.length === 1}
          />
        ))}
      </div>
    </DetailSection>
  );
}

function TrendGroupCard({
  group,
  ticks,
  breaks,
  soleGroup,
}: {
  group: TrendGroup;
  ticks: { key: string; label: string; title: string }[];
  breaks: { afterIndex: number; reason: string }[];
  soleGroup: boolean;
}) {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // "Other" only means anything next to a named group.
  const heading = soleGroup && group.name === UNGROUPED ? "Overall" : group.name;
  // Resolved rather than stored, so a competency that stops appearing — the
  // history refetched, a question was dropped from the form — falls back to
  // the first instead of leaving an empty plot.
  const selected =
    group.competencies.find((c) => c.key === selectedKey) ?? group.competencies[0] ?? null;

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div>
        <h3 className="font-medium text-sm">{heading}</h3>
        <div className="mt-2">
          <TrendLines
            ticks={ticks}
            series={toSeries(group)}
            breaks={breaks}
            tableLabel={`${heading} scores by cycle`}
          />
        </div>
      </div>

      {selected && (
        <div className="space-y-3 border-t pt-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="font-medium text-sm" htmlFor={`trend-competency-${group.key}`}>
              Competency
            </label>
            <Select value={selected.key} onValueChange={setSelectedKey}>
              <SelectTrigger className="w-full sm:w-96" id={`trend-competency-${group.key}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {group.competencies.map((competency) => (
                  <SelectItem key={competency.key} value={competency.key}>
                    {competency.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-muted-foreground text-xs">
              {group.competencies.length}{" "}
              {group.competencies.length === 1 ? "competency" : "competencies"}
            </span>
          </div>
          <TrendLines
            ticks={ticks}
            series={toSeries(selected)}
            breaks={breaks}
            tableLabel={`${selected.name} scores by cycle`}
            showBreakCaptions={false}
          />
        </div>
      )}
    </div>
  );
}
